import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '~/components/Icon';
import { cn } from '~/lib/cn';

interface ObjectiveBlockProps {
  iconName: IconName;
  title: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
}

export function ObjectiveBlock({
  iconName,
  title,
  description,
  isSelected,
  onClick,
}: ObjectiveBlockProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'digitalCampaigns.planWizard.sections.objectives',
  });

  return (
    <div
      data-testid="objective-block-label"
      className={cn(
        'relative w-[297px] cursor-pointer rounded-sm border-px px-lg py-md',
        'text-left transition-all bg-emphasis-default border-emphasis-subtle',
        'flex flex-col justify-start gap-md',
        isSelected
          ? 'bg-default-inverse/subtle border-primary-pressed'
          : 'hover:border-primary-pressed',
      )}
      onClick={onClick}
    >
      {/* Container for Main Icon and Check Icon */}
      <div className="flex w-full justify-between">
        {/* Main Icon (Top Left) */}
        <div className="flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-emphasis-default">
          <Icon
            aria-label={`${t('icon')} ${title}`}
            type={iconName}
            variant="light"
            size="lg"
          />
        </div>

        {/* Selected Checkbox Icon (Top Right) */}
        {isSelected ? (
          <div>
            <Icon
              type="check-circle"
              variant="solid"
              className="text-primary-default"
              aria-label={`${t('iconSelected')} ${title}`}
            />
          </div>
        ) : null}
      </div>

      {/* Title and Description */}
      <div>
        <p className="text-body-sm font-bold">{title}</p>
        <p className="pr-lg text-body-sm">{description}</p>
      </div>
    </div>
  );
}

ObjectiveBlock.displayName = 'ObjectiveBlock';
